import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker, Button, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { r211Api, CalculateResponse } from '../../services/r211Api';
import './R211Form.css';

// ---- Component ----

const R211Form: React.FC = () => {
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CalculateResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // BR-C01: Auto-compute end date (last day of selected month) when user picks a month
  const computedEndDate = selectedDate ? selectedDate.endOf('month') : null;

  const handleDateChange = useCallback((date: Dayjs | null) => {
    setSelectedDate(date);
    setResult(null);
    setErrorMsg(null);
  }, []);

  const handleCalculate = useCallback(async () => {
    if (!selectedDate) {
      setErrorMsg('請選擇計算月份');
      return;
    }

    // Force start date to first day of the selected month
    const startDate = selectedDate.startOf('month').toDate();
    const iso = new Date(
      Date.UTC(startDate.getFullYear(), startDate.getMonth(), 1)
    ).toISOString();

    setLoading(true);
    setResult(null);
    setErrorMsg(null);

    try {
      const resp = await r211Api.calculate(iso);
      setResult(resp);
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { errors?: { message: string }[]; error?: string } } };
      if (anyErr?.response?.data?.errors) {
        const msgs = anyErr.response.data.errors.map((e) => e.message).join('；');
        setErrorMsg(msgs);
      } else if (anyErr?.response?.data?.error) {
        setErrorMsg(anyErr.response.data.error);
      } else {
        setErrorMsg('系統發生錯誤，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fmtDate = (iso: string) => dayjs(iso).format('YYYY-MM-DD');

  return (
    <div className="r211-form">
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">入庫成本計算作業</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>入庫成本計算作業</h1>
            <span className="badge">R211</span>
          </div>
        </div>

        <div className="calc-card">
          <div className="field-row">
            <span className="field-label">入庫日期 :[從]</span>
            <DatePicker
              picker="month"
              value={selectedDate}
              onChange={handleDateChange}
              format="YYYY年MM月"
              placeholder="請選擇月份"
              style={{ width: 200 }}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'month')}
            />
          </div>
          <div className="field-row">
            <span className="field-label">[至]</span>
            <span className="field-value-readonly">
              {computedEndDate ? computedEndDate.format('YYYY-MM-DD') : '—'}
            </span>
          </div>

          {errorMsg && (
            <Alert
              type="error"
              message={errorMsg}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {result && (
            <div className={`result-box ${result.success ? 'success' : 'error'}`}>
              <div className="result-title">
                {result.success ? (
                  <><CheckCircleOutlined style={{ marginRight: 6 }} />計算完成</>
                ) : (
                  <><CloseCircleOutlined style={{ marginRight: 6 }} />計算失敗</>
                )}
              </div>
              <div className="result-detail">
                <div>{result.message}</div>
                <div>計算期間：{fmtDate(result.start_date)} ～ {fmtDate(result.end_date)}</div>
              </div>
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            loading={loading}
            disabled={!selectedDate}
            onClick={handleCalculate}
          >
            計算成本
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default R211Form;
